import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import './SciencePage.css';

export function SciencePage() {
  return (
    <div className="science-page">
      <SEO
        title="The Science of THC Beverages - How Cannabis Drinks Work"
        description="Learn how THC beverages work — nano-emulsion technology, dosing guidelines, onset times, and why people are switching from alcohol to cannabis drinks."
        canonical="/thc-drinks-guide"
      />
      <header className="science-page__hero">
        <h1>The Science of THC Beverages</h1>
        <p className="science-page__subtitle">
          Everything you need to know about cannabis-infused drinks — how they work, dosing,
          timing, and why people are making the switch from alcohol.
        </p>
      </header>

      <section className="science-page__section">
        <h2>What Are THC Drinks?</h2>
        <p>
          THC beverages are cannabis-infused drinks — seltzers, sodas, tonics, and even
          concentrated shots — made with hemp-derived THC that became federally legal under the
          2018 Farm Bill. You can pick them up at stores or order online in most states, no
          dispensary required. Surveys show roughly 77% of people who try them end up drinking
          less alcohol overall.
        </p>
      </section>

      <section className="science-page__section">
        <h2>How They Work</h2>
        <p>
          Most THC drinks use nano-emulsification — a process that breaks cannabinoids into
          tiny, water-compatible particles your body can absorb quickly. On an empty stomach,
          expect to feel something within 15–30 minutes. After a meal, it's more like 30–45
          minutes. Effects typically last 2–4 hours, which is noticeably faster and shorter than
          traditional edibles that can take over an hour to kick in.
        </p>
      </section>

      <section className="science-page__section">
        <h2>Dosing Guide</h2>
        <p>
          Finding the right dose is personal, but here's a quick framework: 2–3 mg is subtle and beginner-friendly — you might barely notice it. At 5 mg, most people hit a relaxed, social sweet spot comparable to a glass of wine. Jump to 10 mg for a solid buzz with deep relaxation, and 15–20 mg is full couch mode for experienced users. The key rule: space your drinks 45–60 minutes apart (not 20 like alcohol) since THC stacks and the second one always hits harder.
        </p>
      </section>

      <section className="science-page__section">
        <h2>Types of THC Drinks</h2>
        <ul className="science-page__list">
          <li>
            <strong>Seltzers</strong> — Light, low-calorie, easy to sip. The most popular
            format for casual use.
          </li>
          <li>
            <strong>Social Tonics</strong> — Complex herbal or botanical flavors with
            lower doses, designed for slow sipping.
          </li>
          <li>
            <strong>Shots</strong> — Concentrated and fast-acting. Good when you want
            effects without committing to a full drink.
          </li>
          <li>
            <strong>Mixes & Drops</strong> — DIY-friendly. Add them to any beverage you
            already enjoy.
          </li>
          <li>
            <strong>Sodas</strong> — Nostalgic flavors (cola, root beer, lemon-lime) with
            a cannabis twist.
          </li>
          <li>
            <strong>Cocktail Alternatives</strong> — Margarita, mojito, and paloma vibes
            without the booze or the hangover.
          </li>
        </ul>
      </section>

      <section className="science-page__section">
        <h2>Health Comparison vs. Alcohol</h2>
        <p>Here's what draws people toward THC drinks over traditional alcohol:</p>
        <ul className="science-page__list">
          <li>No hangover — wake up feeling normal</li>
          <li>Fewer calories (typically 0–25 per serving vs. 150–200 for beer/cocktails)</li>
          <li>No evidence of liver damage with moderate use</li>
          <li>Cannabis is not classified as a carcinogen (alcohol is)</li>
          <li>Most people report staying more mentally present and in control</li>
        </ul>
        <p className="science-page__caveat">
          <strong>Worth noting:</strong> THC can trigger anxiety in some people, especially at
          higher doses. It's not recommended during pregnancy, and it will show up on drug
          tests. Start low and see how your body responds.
        </p>
      </section>

      <section className="science-page__section">
        <h2>Legal Status</h2>
        <p>
          Hemp-derived THC is federally legal in the United States under the 2018 Farm Bill, as
          long as the product contains less than 0.3% THC by dry weight. It's the same THC
          molecule found in marijuana — the legal distinction is simply the source plant and
          concentration. These products are available at regular retailers in most states, though
          a handful have enacted local restrictions.
        </p>
      </section>

      <section className="science-page__section">
        <h2>Drug Testing</h2>
        <p>
          Yes — THC beverages will show up on a drug test. THC is THC regardless of whether it
          came from hemp or marijuana. Standard urine tests can detect it anywhere from 3 to 30
          days after use depending on how often you consume. If you're subject to testing, plan
          accordingly.
        </p>
      </section>

      <section className="science-page__section">
        <h2>Tips for First-Timers</h2>
        <ul className="science-page__list">
          <li>Start with 2–5 mg — you can always have more next time</li>
          <li>Try it on an empty stomach for faster, more predictable onset</li>
          <li>Wait a full 30 minutes before deciding it "didn't work"</li>
          <li>Don't double up — the most common complaint is taking a second dose too soon</li>
          <li>Have water and snacks handy, and stay somewhere comfortable</li>
        </ul>
        <p className="science-page__tip">
          <strong>The classic mistake:</strong> "I don't feel anything" at the 15-minute mark,
          so you grab another. Then both hit at once. Patience is your friend here.
        </p>
      </section>

      <section className="science-page__section science-page__cta-section">
        <h2>Ready to Explore?</h2>
        <p>Browse our collection of THC beverages and find your perfect match.</p>
        <Link to="/thc-drinks" className="science-page__cta">
          Check Out Our Collection
        </Link>
      </section>
    </div>
  );
}
